import {
  DEFAULT_RADIO_PREFERENCES,
  normalizeRadioPreferences,
  radioStationById,
  type RadioPreferences,
  type RadioStationId,
} from "../config/radio.ts";

export type RadioPlaybackState =
  | "off"
  | "ready"
  | "loading"
  | "playing"
  | "blocked"
  | "error";

export interface RadioStatus {
  readonly stationId: RadioStationId | null;
  readonly volume: number;
  readonly muted: boolean;
  readonly playbackState: RadioPlaybackState;
  readonly errorMessage: string | null;
}

export interface RadioAudioElement {
  src: string;
  preload: string;
  volume: number;
  muted: boolean;
  readonly error: MediaError | null;
  play(): Promise<void>;
  pause(): void;
  load(): void;
  removeAttribute(name: string): void;
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
}

export interface CreateBrowserRadioSystemOptions {
  readonly audioFactory?: () => RadioAudioElement | null;
  readonly initialPreferences?: Readonly<RadioPreferences>;
  readonly onPreferencesChange?: (
    preferences: Readonly<RadioPreferences>,
  ) => void;
}

export interface VilniusRadioSystem {
  selectStation(stationId: RadioStationId | null): Promise<boolean>;
  setVolume(volume: number): void;
  setMuted(muted: boolean): void;
  resume(): Promise<boolean>;
  status(): RadioStatus;
  subscribe(listener: (status: Readonly<RadioStatus>) => void): () => void;
  dispose(): void;
}

function defaultAudioFactory(): RadioAudioElement | null {
  return typeof Audio === "undefined" ? null : new Audio();
}

function playErrorState(error: unknown): Readonly<{
  playbackState: "blocked" | "error";
  errorMessage: string;
}> {
  if (
    error instanceof DOMException &&
    error.name === "NotAllowedError"
  ) {
    return {
      playbackState: "blocked",
      errorMessage: "Press play again to allow radio audio.",
    };
  }
  return {
    playbackState: "error",
    errorMessage: "This station is not answering right now.",
  };
}

export function createBrowserRadioSystem(
  options: Readonly<CreateBrowserRadioSystemOptions> = {},
): VilniusRadioSystem {
  const audio = (options.audioFactory ?? defaultAudioFactory)();
  let preferences = normalizeRadioPreferences(
    options.initialPreferences ?? DEFAULT_RADIO_PREFERENCES,
  );
  let state: RadioStatus = {
    stationId: preferences.stationId,
    volume: preferences.volume,
    muted: false,
    playbackState: preferences.stationId === null ? "off" : "ready",
    errorMessage: null,
  };
  let disposed = false;
  let playbackRevision = 0;
  const listeners = new Set<(status: Readonly<RadioStatus>) => void>();

  const notify = (): void => {
    const snapshot = { ...state };
    for (const listener of listeners) {
      listener(snapshot);
    }
  };
  const updateState = (next: Partial<RadioStatus>): void => {
    state = { ...state, ...next };
    notify();
  };
  const savePreferences = (): void => {
    options.onPreferencesChange?.({ ...preferences });
  };
  const configureSource = (stationId: RadioStationId): boolean => {
    if (audio === null) {
      updateState({
        playbackState: "error",
        errorMessage: "Radio playback is unavailable in this browser.",
      });
      return false;
    }
    const streamUrl = radioStationById(stationId).streamUrl;
    if (audio.src !== streamUrl) {
      audio.pause();
      audio.src = streamUrl;
      audio.preload = "none";
      audio.load();
    }
    audio.volume = preferences.volume;
    audio.muted = state.muted;
    return true;
  };

  const onPlaying = (): void => {
    if (!disposed && state.stationId !== null) {
      updateState({ playbackState: "playing", errorMessage: null });
    }
  };
  const onWaiting = (): void => {
    if (!disposed && state.stationId !== null) {
      updateState({ playbackState: "loading", errorMessage: null });
    }
  };
  const onError = (): void => {
    if (!disposed && state.stationId !== null) {
      const mediaCode = audio?.error?.code;
      updateState({
        playbackState: "error",
        errorMessage: mediaCode === undefined
          ? "This station is not answering right now."
          : `This station could not be played (media error ${mediaCode}).`,
      });
    }
  };

  audio?.addEventListener("playing", onPlaying);
  audio?.addEventListener("waiting", onWaiting);
  audio?.addEventListener("stalled", onWaiting);
  audio?.addEventListener("error", onError);
  if (audio !== null) {
    audio.preload = "none";
    audio.volume = preferences.volume;
    audio.muted = false;
  }

  const beginPlayback = async (): Promise<boolean> => {
    if (disposed || state.stationId === null) {
      return false;
    }
    const stationId = state.stationId;
    const revision = ++playbackRevision;
    if (!configureSource(stationId)) {
      return false;
    }
    updateState({ playbackState: "loading", errorMessage: null });
    try {
      await audio!.play();
      if (disposed || revision !== playbackRevision || state.stationId !== stationId) {
        return false;
      }
      updateState({ playbackState: "playing", errorMessage: null });
      return true;
    } catch (error) {
      if (disposed || revision !== playbackRevision || state.stationId !== stationId) {
        return false;
      }
      updateState(playErrorState(error));
      return false;
    }
  };

  if (preferences.stationId !== null) {
    configureSource(preferences.stationId);
  }

  return {
    async selectStation(stationId): Promise<boolean> {
      if (disposed) {
        return false;
      }
      playbackRevision += 1;
      preferences = { ...preferences, stationId };
      savePreferences();
      if (stationId === null) {
        audio?.pause();
        if (audio !== null) {
          audio.removeAttribute("src");
          audio.load();
        }
        updateState({
          stationId: null,
          playbackState: "off",
          errorMessage: null,
        });
        return true;
      }
      updateState({
        stationId,
        playbackState: "ready",
        errorMessage: null,
      });
      return beginPlayback();
    },

    setVolume(volume): void {
      if (disposed) {
        return;
      }
      preferences = normalizeRadioPreferences({
        ...preferences,
        volume,
      });
      if (audio !== null) {
        audio.volume = preferences.volume;
      }
      savePreferences();
      updateState({ volume: preferences.volume });
    },

    setMuted(muted): void {
      if (disposed) {
        return;
      }
      if (audio !== null) {
        audio.muted = muted;
      }
      updateState({ muted });
    },

    resume(): Promise<boolean> {
      return beginPlayback();
    },

    status(): RadioStatus {
      return { ...state };
    },

    subscribe(listener): () => void {
      if (disposed) {
        return () => {};
      }
      listeners.add(listener);
      listener({ ...state });
      return () => listeners.delete(listener);
    },

    dispose(): void {
      if (disposed) {
        return;
      }
      disposed = true;
      playbackRevision += 1;
      listeners.clear();
      if (audio !== null) {
        audio.removeEventListener("playing", onPlaying);
        audio.removeEventListener("waiting", onWaiting);
        audio.removeEventListener("stalled", onWaiting);
        audio.removeEventListener("error", onError);
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
      }
    },
  };
}
